import React from 'react';
import { TouchableOpacity } from 'react-native';

import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { TaskPriority } from '@graphql/generated';
import { useHover } from '@hooks/useHover';
import theme from '@themes/theme';

type PriorityOption = {
  iconName: React.ComponentProps<typeof Icon>['name'];
  title: string;
  color: keyof typeof theme.colors;
  id: TaskPriority;
};

const PriorityRow = ({
  option: { iconName, title, color, id },
  onPress,
}: {
  option: PriorityOption;
  onPress: (priority: TaskPriority) => void;
}) => {
  const { isHovered, hoverProps } = useHover();
  return (
    <TouchableOpacity {...hoverProps} onPress={() => onPress(id)}>
      <Box
        p='xs'
        flex={1}
        flexDirection='row'
        height={32}
        alignItems='center'
        backgroundColor={isHovered ? 'grey01' : 'white'}>
        <Icon name={iconName} color={color} variant='s' />
        <Text
          ml='xs'
          accessibilityLabel={title}
          variant='webBodySecondary'
          color={color}
          numberOfLines={1}>
          {title}
        </Text>
      </Box>
    </TouchableOpacity>
  );
};

export const PrioritySelector = ({
  onPrioritySelect,
}: {
  onPrioritySelect: (priority: TaskPriority) => void;
}) => {
  const priorityOptions: PriorityOption[] = [
    {
      iconName: 'ChevronsUp',
      title: 'High',
      color: 'alertRed',
      id: TaskPriority.High,
    },
    {
      iconName: 'Equals',
      title: 'Medium',
      color: 'pastelYellow',
      id: TaskPriority.Medium,
    },
    {
      iconName: 'ChevronsDown',
      title: 'Low',
      color: 'blue',
      id: TaskPriority.Low,
    },
  ];

  const handlePriorityPress = (priorityId: TaskPriority) => {
    onPrioritySelect?.(priorityId);
  };

  return (
    <Box
      width={120}
      py='xxs'
      style={{
        borderRadius: 8,
        borderWidth: 1,
        borderColor: theme.colors.grey02,
      }}>
      {priorityOptions.map((option) => (
        <PriorityRow
          key={option.id}
          option={option}
          onPress={handlePriorityPress}
        />
      ))}
    </Box>
  );
};
